import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {FormFancyDropDown} from '@techsee/techsee-ui-common/lib/forms/form-fancy-drop-down';
import {ICountrySelectController} from './ts-country-select.controller';
import {SimpleFieldModel} from '@techsee/techsee-ui-common/lib/forms/_shared/simple-field-model';

import './styles.scss';

export interface ICountrySelectProps {
    ctrl: ICountrySelectController;
}

// Custom field model that handles selection
class CountrySelectFieldModel extends SimpleFieldModel {
    private onSelectCallback?: (country: any) => void;
    private isInitialized: boolean = false;

    setOnSelect(callback: (country: any) => void) {
        this.onSelectCallback = callback;
    }

    setValue(value: any) {
        super.setValue(value);

        // Always trigger callback if component is initialized and we have a value
        if (this.onSelectCallback && value && this.isInitialized) {
            setTimeout(() => {
                this.onSelectCallback!(value);
            }, 0);
        }
    }

    markAsInitialized() {
        this.isInitialized = true;
    }
}

@observer
export class TsCountrySelect extends TechseeBaseComponent<ICountrySelectProps> {
    private countryCodeField: CountrySelectFieldModel;

    constructor(props: ICountrySelectProps) {
        super(props);
        this.countryCodeField = new CountrySelectFieldModel({
            value: props.ctrl.countryCodeText || '',
            placeholder: props.ctrl.countryCodesPlaceHolder || ''
        });

        // Set up the selection callback
        this.countryCodeField.setOnSelect((value) => {
            // Find all countries with this dial code
            const matchingCountries = this.props.ctrl.filteredCountries.filter((country) => country.dial === value);

            // If there's only one match, use it
            if (matchingCountries.length === 1) {
                this.props.ctrl.onCountrySelect(matchingCountries[0]);
            } else if (matchingCountries.length > 1) {
                // For multiple countries with same dial code, we need a different approach
                // Let's use the "more countries" option to show a sub-selection
                // For now, just use the first match, but ideally show a sub-menu
                this.props.ctrl.onCountrySelect(matchingCountries[0]);
            }
        });
    }

    componentDidMount() {
        // Initialize the controller
        this.props.ctrl.init();

        // Set default country if none is selected
        if (!this.props.ctrl.countryCodeText && this.props.ctrl.filteredCountries.length > 0) {
            const firstCountry = this.props.ctrl.filteredCountries[0];
            this.countryCodeField.setValue(firstCountry.dial);
            this.props.ctrl.onCountrySelect(firstCountry);
        }
        // Mark the field as initialized after a short delay
        setTimeout(() => {
            this.countryCodeField.markAsInitialized();
        }, 100);
    }

    componentDidUpdate(prevProps: ICountrySelectProps) {
        // Update field value when countryCodeText changes
        if (prevProps.ctrl.countryCodeText !== this.props.ctrl.countryCodeText) {
            this.countryCodeField.setValue(this.props.ctrl.countryCodeText || '');
        }
    }

    private CountryOptionComponent = ({data, currentSelIndex, index}: any): JSX.Element => {
        const {ctrl} = this.props;
        const isMoreCountries = data.display === ctrl.moreCountryCodes;
        const isAdminSelect = data.adminSelect;

        return (
            <div
                className={`item ${currentSelIndex === index ? 'focused' : ''}`}
                onClick={() => this.props.ctrl.onCountrySelect(data)}>
                {!isMoreCountries ? (
                    <div className={'item'}>
                        <img src={data.flag} alt='' />
                        <span className={isAdminSelect ? 'admin-select' : ''}>
                            {data.display} ({data.dial})
                        </span>
                    </div>
                ) : (
                    <span className='more-countries-span' onClick={() => this.props.ctrl.onCountrySelect(data)}>
                        {ctrl.moreCountryCodes}
                    </span>
                )}
            </div>
        );
    };

    renderInternal(): JSX.Element {
        const {ctrl} = this.props;
        const {useNewInterface, sms, addCountryCodeToSms, countryCodesNoMatch} = this.props.ctrl;

        if (!addCountryCodeToSms) {
            return <></>;
        }

        return (
            <div className={`ts-country-select ${useNewInterface ? 'bright-country' : ''}`}>
                <FormFancyDropDown
                    className='country-codes-dropdown'
                    uid='country-codes'
                    options={ctrl.filteredCountries}
                    optionComponent={this.CountryOptionComponent}
                    model={this.countryCodeField}
                    optionValueKey='dial' // Keep dial for display
                    searchKeys={['display', 'dial']}
                    noResultsText={countryCodesNoMatch}
                    disabled={sms}
                />
            </div>
        );
    }
}
